import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BlogCard = ({ post }) => {
  const navigate = useNavigate();

  // Navigate to detail page
  const handleReadMore = (slug) => {
    navigate(`./${slug}`);
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

  const formatNumber = (num) =>
    num >= 1000 ? (num / 1000).toFixed(1) + 'k' : num.toString();

  return (
    <article className="bg-surface rounded-lg card-shadow hover:card-shadow-lg transition-all duration-300 overflow-hidden group">

      {/* Image / Banner */}
      <div
        className="relative overflow-hidden h-48 cursor-pointer"
        onClick={() => handleReadMore(post.slug)}
      >
        <Image
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />

        {/* Featured Tag */}
        {post.featured && (
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent text-white">
              <Icon name="Star" size={12} className="mr-1" />
              Featured
            </span>
          </div>
        )}

        {/* Category Tag */}
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary text-white">
            {post.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {post.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors duration-150"
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* Title */}
        <h3
          className="text-xl font-heading font-semibold text-text-primary mb-3 line-clamp-2 group-hover:text-primary transition-colors duration-150 cursor-pointer"
          onClick={() => handleReadMore(post.slug)}
        >
          {post.title}
        </h3>

        {/* Excerpt */}
        <p className="text-text-secondary mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Author & Date */}
        {post.author && post.author.name !== 'Unknown Author' && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <Image
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {post.author.name}
                </p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-sm text-text-secondary">
                {formatDate(post.publishedAt)}
              </p>
              <p className="text-xs text-text-secondary">{post.readTime}</p>
            </div>
          </div>
        )}

        {/* author name is null so show Date and Read Time    */}
        {(!post.author || post.author.name === 'Unknown Author') && (
          <div className="flex justify-end mb-4">
            <div className="text-right">
              <p className="text-sm text-text-secondary">
                {formatDate(post.publishedAt)}
              </p>
              <p className="text-xs text-text-secondary">{post.readTime} </p>
            </div>
          </div>
        )}

        {/* Footer: Views / Comments / Shares etc. */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-4 text-sm text-text-secondary">
            <div className="flex items-center space-x-1">
              <Icon name="Eye" size={16} />
              <span>{formatNumber(post.views)}</span>
            </div>

            <div className="flex items-center space-x-1">
              <Icon name="MessageCircle" size={16} />
              <span>{post.comments}</span>
            </div>

            <div className="flex items-center space-x-1">
              <Icon name="Share2" size={16} />
              <span>{post.shares}</span>
            </div>
          </div>

          {/* Read More Button */}
          <button
            className="flex items-center space-x-1 text-sm text-primary hover:text-primary-700 font-medium transition-colors duration-150"
            onClick={() => handleReadMore(post.slug)}
          >
            <span>Read More</span>
            <Icon name="ArrowRight" size={16} />
          </button>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;